import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Bookmark, Building2, User, X } from 'lucide-react'
import { searchCompanies, searchPersons, type PersonSummary } from '../../services/apiClient'
import type { CVRCompany } from '../../types'
import { useWatchlist } from '../../contexts/WatchlistContext'
import { flattenWatchlist } from '../../services/watchlist'
import { CountryFlagIcon } from '../ui'
import { buildSuggestions } from './pickerSuggestions'

export interface PickedEntity {
  /** »person:<enhedsnr>« or »cvr:<cvr>« */
  id: string
  name: string
}

interface EntityPickerProps {
  label: string
  value: PickedEntity | null
  onChange: (value: PickedEntity | null) => void
}

/**
 * Pick a person or company by name or CVR (8 digits), with suggestions
 * from both searches. Watched entities that match come first, straight
 * away; each row says enough (role, city, CVR) to pick the right namesake.
 */
const EntityPicker: React.FC<EntityPickerProps> = ({ label, value, onChange }) => {
  const id = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hits, setHits] = useState<{ companies: CVRCompany[]; persons: PersonSummary[] }>({
    companies: [],
    persons: [],
  })
  const [active, setActive] = useState(-1)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const { data, lists } = useWatchlist()
  const watchItems = useMemo(() => flattenWatchlist(data), [data])
  const q = query.trim()
  const suggestions = useMemo(
    () => (q.length < 2 ? [] : buildSuggestions(q, hits.companies, hits.persons, watchItems, lists)),
    [q, hits, watchItems, lists]
  )

  // New suggestions: nothing highlighted until the arrow keys are used.
  useEffect(() => setActive(-1), [suggestions])

  useEffect(() => {
    if (active < 0) return
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  const pick = (s: { id: string; name: string }) => {
    onChange({ id: s.id, name: s.name })
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = suggestions.length
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (n === 0) return
      e.preventDefault()
      setOpen(true)
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (i < 0 ? (step === 1 ? 0 : n - 1) : (i + step + n) % n))
    } else if (e.key === 'Enter') {
      // Enter takes the highlighted row, or the top one — never submits the form half-filled.
      if (open && n > 0) {
        e.preventDefault()
        pick(suggestions[Math.max(0, active)])
      }
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
      setActive(-1)
    }
  }

  useEffect(() => {
    if (q.length < 2) {
      setHits({ companies: [], persons: [] })
      return
    }
    setOpen(true)
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      try {
        const [companies, persons] = await Promise.all([
          searchCompanies(q, 1, 5).catch(() => ({ companies: [] })),
          /^\d{8}$/.test(q) ? Promise.resolve({ persons: [] }) : searchPersons(q, 1, 5).catch(() => ({ persons: [] })),
        ])
        if (cancelled) return
        setHits({ companies: companies.companies, persons: persons.persons })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [q])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const isPerson = value?.id.startsWith('person:')

  return (
    <div ref={wrapRef} className="relative min-w-0 flex-1">
      <label htmlFor={id} className="mb-1 block text-[13px] font-semibold text-ink-500">
        {label}
      </label>
      {value ? (
        <div className="flex h-11 items-center gap-2 rounded-lg border border-rule-strong bg-paper px-3">
          {isPerson ? (
            <User className="h-4 w-4 shrink-0 text-ink-500" aria-hidden />
          ) : (
            <Building2 className="h-4 w-4 shrink-0 text-ink-500" aria-hidden />
          )}
          <span className="min-w-0 flex-1 truncate font-semibold text-ink">{value.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-500 hover:bg-rule-soft hover:text-ink"
            aria-label={`Fjern ${value.name}`}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : (
        <input
          id={id}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Navn eller CVR-nummer"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${id}-opt-${active}` : undefined}
          className="h-11 w-full rounded-lg border border-rule-strong bg-paper px-3 text-[15px] text-ink placeholder:text-ink-400"
        />
      )}
      {!value && open && (suggestions.length > 0 || loading) && (
        <ul
          ref={listRef}
          id={`${id}-list`}
          role="listbox"
          className="absolute inset-x-0 top-full z-40 mt-1 max-h-72 overflow-y-auto rounded-lg border border-rule bg-paper py-1 shadow-[0_8px_24px_rgba(18,21,26,0.12)]"
        >
          {loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-sm text-ink-500">Søger…</li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              id={`${id}-opt-${i}`}
              data-index={i}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(s)}
              className={`flex cursor-pointer items-start gap-2.5 px-3 py-2 ${i === active ? 'bg-rule-soft' : ''} ${
                !s.watched && suggestions[i - 1]?.watched ? 'mt-1 border-t border-rule-soft' : ''
              }`}
            >
              {s.kind === 'person' ? (
                <User className="mt-[3px] h-4 w-4 shrink-0 text-ink-400" aria-label="Person" />
              ) : (
                <Building2 className="mt-[3px] h-4 w-4 shrink-0 text-ink-400" aria-label="Virksomhed" />
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-1.5">
                  <span className="truncate font-semibold text-ink">{s.name}</span>
                  {s.countryCode && <CountryFlagIcon code={s.countryCode} focusable={false} />}
                  {s.watched && (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-active-700"
                      title={s.watchLists.length > 0 ? `På watchlist: ${s.watchLists.join(', ')}` : 'På watchlist'}
                    >
                      <Bookmark className="h-3 w-3 fill-current" aria-hidden />
                      {s.watchLists[0] ?? 'Watchlist'}
                    </span>
                  )}
                </span>
                <span className="block text-[13px] leading-snug text-ink-500">{s.meta}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default EntityPicker
